import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

export interface MaxLoanBalanceRow {
  acc_code?: string;
  acc_desc?: string;
  py_bal?: string | number;
  trialBal_submitted?: string | number;
  reclass_adjust?: string | number;
  net_amount?: string | number;
  max_bal?: string | number;
  [key: string]: unknown;
}

@Component({
    selector: 'gtw-max-loan-balance',
    templateUrl: './max-loan-balance.component.html',
    styleUrls: ['./max-loan-balance.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MaxLoanBalanceComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly contextSubs = new Subscription();
  private isInitialized = false;

  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  title = 'Maximum Loan Balance';
  minimise = false;
  loading = false;
  displayTable = true;
  tableData: MaxLoanBalanceRow[] = [
    { acc_code: '001021000', acc_desc: 'Trade or Accounts Receivable - Related Parties - Interest Bearing', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '001021001', acc_desc: 'Trade or Accounts Receivable - Related Parties - Non-Interest Bearing', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '001021005', acc_desc: 'A/R - Related US', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '001061000', acc_desc: 'Loans and Advances to Related U.S. companies', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '20' },
    { acc_code: '001061000', acc_desc: 'Loans and Advances to Related U.S. companies', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '001061001', acc_desc: 'Loans and Advances to Related Non-U.S. companies', py_bal: '0', trialBal_submitted: '16,688', reclass_adjust: '0', net_amount: '16,688', max_bal: '8,344' },
    { acc_code: '001061001', acc_desc: 'Loans and Advances to Related Non-U.S. companies', py_bal: '0', trialBal_submitted: '14,443', reclass_adjust: '0', net_amount: '14,443', max_bal: '7,221' },
    { acc_code: '002011000', acc_desc: 'Trade or Accounts Payable - Related Parties', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '002011005', acc_desc: 'A/P - Related US', py_bal: '0', trialBal_submitted: '48,323', reclass_adjust: '0', net_amount: '48,323', max_bal: '24,161' },
    { acc_code: '002031000', acc_desc: 'Borrowing from related U.S. companies (excluding trade payables)', py_bal: '0', trialBal_submitted: '14,442', reclass_adjust: '0', net_amount: '14,442', max_bal: '7,221' },
    { acc_code: '002031000', acc_desc: 'Borrowing from related U.S. companies (excluding trade payables)', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' },
    { acc_code: '002032000', acc_desc: 'Borrowing from related non-U.S. companies (excluding trade payables)', py_bal: '0', trialBal_submitted: '-63,251,534', reclass_adjust: '0', net_amount: '-63,251,534', max_bal: '-94,991,359' },
    { acc_code: '002032000', acc_desc: 'Borrowing from related non-U.S. companies (excluding trade payables)', py_bal: '0', trialBal_submitted: '0', reclass_adjust: '0', net_amount: '0', max_bal: '0' }
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.refreshFromWorkspace();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.refreshFromWorkspace()),
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.refreshFromWorkspace()),
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private refreshFromWorkspace(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.touchView();
  }

  /** OnPush: refresh when workspace context settles (speed-up / custom-element safe). */
  private touchView(): void {
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG) {
      return;
    }
    this.isInitialized = true;
    this.displayTable = true;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  get tableHeaders(): string[] {
    return ['acc_code', 'acc_desc', 'py_bal', 'trialBal_submitted', 'reclass_adjust', 'net_amount', 'max_bal'];
  }
}
